import {
  KeysTabsAnalysis,
  LabelKeys,
  LabelTypesOptions,
  TypesPreparationAT,
} from "@app/types/samples.types";
import { toExponentialUppercase } from "@app/utils/functions";
import { filterAndSortData, formatValueWithCustomRounding } from "../helpers";

/**
 * Formatea valores de salida con truncamiento especial para campos específicos
 */
export const formatOutputValue = (
  value: any,
  type: string,
  decimals: number,
  fieldKey: string
): string => {
  // Solo aplicar truncamiento a cpm_fondo y cpm_fondo_beta
  if (
    (fieldKey === "cpm_fondo" || fieldKey === "cpm_fondo_beta") &&
    type === LabelTypesOptions.NUMERICO
  ) {
    const numericValue = parseFloat(value);
    if (isNaN(numericValue)) {
      return value;
    }

    const multiplier = Math.pow(10, decimals);
    const shifted = numericValue * multiplier;
    const truncated = Math.trunc(shifted) / multiplier;

    return truncated.toFixed(decimals);
  }

  return formatValueWithCustomRounding(value, type, decimals);
};

/**
 * Determina el tipo de análisis considerando preparación AT
 */
export const determineAnalysisType = (dataObjects: any) => {
  const typeAnalysis = dataObjects.typeAnalysis;
  const codeTypeAnalisis = typeAnalysis?.codigo;

  if (codeTypeAnalisis === KeysTabsAnalysis.AT) {
    if (dataObjects.typePreparation.name === TypesPreparationAT.E) {
      return {
        codigo: KeysTabsAnalysis.AT_E,
        descripcion: typeAnalysis.descripcion,
      };
    }
    if (dataObjects.typePreparation.name === TypesPreparationAT.C) {
      return {
        codigo: KeysTabsAnalysis.AT_C,
        descripcion: typeAnalysis.descripcion,
      };
    }
    if (dataObjects.typePreparation.name === TypesPreparationAT.PP) {
      return {
        codigo: KeysTabsAnalysis.AT_PP,
        descripcion: typeAnalysis.descripcion,
      };
    }
  }

  return typeAnalysis;
};

/**
 * Verifica si la muestra tiene ambos tipos BT y BR
 */
export const hasBothBTBR = (dataObjects: any): boolean => {
  const typesAllAnalisis = dataObjects?.analysis_type?.map(
    (item: any) => item.codigo
  );
  return (
    typesAllAnalisis?.includes(KeysTabsAnalysis.BT) &&
    typesAllAnalisis?.includes(KeysTabsAnalysis.BR)
  );
};

/**
 * Verifica si la muestra tiene ambos tipos Sr89 y Sr90
 */
export const hasBothSr89Sr90 = (dataObjects: any): boolean => {
  const typesAllAnalisis = dataObjects?.analysis_type?.map(
    (item: any) => item.codigo
  );
  return (
    typesAllAnalisis?.includes(KeysTabsAnalysis.Sr89) &&
    typesAllAnalisis?.includes(KeysTabsAnalysis.Sr90)
  );
};

/**
 * Prepara los datos formateados para el PDF
 */
export const prepareFormattedData = (resultsAll: any) => {
  const formattedData = {
    ...resultsAll,
    calculation_id: resultsAll?.calculation?.id,
  };
  delete formattedData.calculation;
  return formattedData;
};

/**
 * Procesa y formatea los resultados para mostrar en el PDF
 */
export const processResults = (
  results: any[],
  toExponential: boolean = true
) => {
  return results.map((result) => ({
    label: result.label,
    value: toExponential
      ? toExponentialUppercase(result.value, 4)
      : result.value,
  }));
};

/**
 * Determina el nombre del archivo PDF
 */
export const generatePDFFileName = (sampleRegistration: string): string => {
  return `Report_${sampleRegistration}.pdf`;
};

/**
 * Extrae y procesa los datos de salida para el PDF
 */
export const extractOutputData = (
  formattedData: any,
  isCurrier: boolean,
  typeAnalysisCode: string
) => {
  return filterAndSortData(formattedData, isCurrier, typeAnalysisCode);
};

/**
 * Verifica si se debe usar el método Currie
 */
export const isCurrieMethod = (dataObjects: any): boolean => {
  return (
    dataObjects?.calculation_method &&
    dataObjects?.calculation_method === "CURRIE"
  );
};
